import * as brokerHooks from "@rentiohq/shared-frontend/dist/redux/broker/broker.hooks";
import { generateFormId } from "@rentiohq/shared-frontend/dist/redux/form/form.utils";
import * as propertySelectors from "@rentiohq/shared-frontend/dist/reduxV2/property/property.selectors";
import { getPropertyTypeString } from "@rentiohq/shared-frontend/dist/reduxV2/property/property.utils";
import {
  AMENITY_NAMES,
  getAmenityBooleanValue,
  getAmenityNumberValue,
  getAmenityStringValue,
  getDataTypeFromValue,
} from "@rentiohq/shared-frontend/dist/reduxV2/propertyAmenity";
import * as propertyAmenityApi from "@rentiohq/shared-frontend/dist/reduxV2/propertyAmenity/propertyAmenity.api";
import { useGetByPropertyId } from "@rentiohq/shared-frontend/dist/reduxV2/propertyAmenity/propertyAmenity.hooks";
import { EBrokerFeature } from "@rentiohq/shared-frontend/dist/types/broker.types";
import {
  EElectricityType,
  ENaturalGasAvailable,
  EPropertyEPCLabel,
  EPropertyMemberTypes,
  EPropertyTypeId,
} from "@rentiohq/shared-frontend/dist/types/property.types";
import { IPropertyAmenity } from "@rentiohq/shared-frontend/dist/types/propertyAmenity.types";
import { showAlert } from "@rentiohq/shared-frontend/dist/utils/alert/alert.utils";
import { formatDate } from "@rentiohq/shared-frontend/dist/utils/date.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { formatCurrency } from "@rentiohq/shared-frontend/dist/utils/number.utils";
import { iHaveOneOfRoles } from "@rentiohq/shared-frontend/dist/utils/roles.utils";
import {
  Button,
  Card,
  Grid,
  MultiStepForm,
  ResourceList,
  ResourceListItem,
  TextStyle,
} from "@rentiohq/web-shared/dist/components";
import {
  isPerformingSubmitActionSelector,
  performingSubmitActionErrorSelector,
  submitActionCreator,
} from "forms/editPropertyExtraInfo/schema.editPropertyExtraInfo.actions";
import {
  getOptionLabel,
  TOptionSet,
} from "forms/editPropertyExtraInfo/schema.editPropertyExtraInfo.options";
import { EExtraInfoGroup } from "forms/editPropertyExtraInfo/schema.editPropertyExtraInfo.types";
import {
  parseAmenityDate,
  propertyTypeCanHaveAdjoinedType,
  propertyTypeCanHaveFloors,
} from "forms/editPropertyExtraInfo/schema.editPropertyExtraInfo.utils";
import { compact } from "lodash";
import React from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { IRootStore } from "redux/reducers";
import createEditPropertyExtraInfoForm from "../../../../forms/editPropertyExtraInfo";
import { propertyExtraOtherAmenitiesToastSuccess } from "../../../../services/translationService";
import { PropertyPhotoGalleryModal } from "../Detail/components/PropertyPhotoGalleryModal";
import {
  BulkAmenityEditorModal,
  IEditableAmenity,
} from "./components/BulkAmenityEditorModal";

interface IExtraCardItem {
  title: string;
  extraInfoGroup: EExtraInfoGroup;
  items: {
    title: string;
    value?: string | number | boolean;
    /** Rows that lead somewhere instead of stating something. The photos are
     * kept in a gallery of their own, and this is the line that opens it. */
    onClick?: () => void;
  }[];
  editable: boolean;
  clearable: boolean;
}

const formId = generateFormId();

// Reading one amenity for one row. The stored value carries its own type, so
// each of these asks for the type the row is going to show and hands back
// `undefined` when nothing is stored, which the card draws as a dash.
const amenityBoolean = (
  amenities: IPropertyAmenity[] | undefined,
  name: string,
) => getAmenityBooleanValue(amenities, name) ?? undefined;

const amenityNumber = (
  amenities: IPropertyAmenity[] | undefined,
  name: string,
) => getAmenityNumberValue(amenities, name) ?? undefined;

const amenityText = (amenities: IPropertyAmenity[] | undefined, name: string) =>
  getAmenityStringValue(amenities, name) ?? undefined;

const amenityMeasure = (
  amenities: IPropertyAmenity[] | undefined,
  name: string,
  unit: string,
) => {
  const value = getAmenityNumberValue(amenities, name);
  return value === null ? undefined : `${value} ${unit}`;
};

const amenityOption = (
  amenities: IPropertyAmenity[] | undefined,
  name: string,
  set: TOptionSet,
) => getOptionLabel(set, getAmenityStringValue(amenities, name));

const amenityDate = (
  amenities: IPropertyAmenity[] | undefined,
  name: string,
) => {
  const parsed = parseAmenityDate(getAmenityStringValue(amenities, name));
  return parsed ? formatDate(parsed) : undefined;
};

export const Extra = () => {
  const params = useParams<{ propertyId: string }>();
  const propertyId = params.propertyId ? +params.propertyId : undefined;

  const property = useSelector((state: IRootStore) =>
    propertyId ? propertySelectors.getDetail(state, propertyId) : undefined,
  );
  const isGroup = property?.typeId === EPropertyTypeId.Group;
  const [showPhotoGallery, setShowPhotoGallery] = React.useState(false);

  const hasBookkeepingFeature = brokerHooks.useBrokerFeature(
    EBrokerFeature.Bookkeeping,
  );

  // Fetch property amenities
  const { items: propertyAmenities, refetch: refetchPropertyAmenities } =
    useGetByPropertyId({
      propertyId,
      shouldRefetch: true,
    });

  const [extraInfoGroup, setExtraInfoGroup] = React.useState<
    EExtraInfoGroup | undefined
  >();

  // Bulk amenity editor modal state
  const [isBulkAmenityModalOpen, setIsBulkAmenityModalOpen] =
    React.useState(false);

  // Get custom amenities (those not starting with "rentio.default")
  const customAmenities = React.useMemo(() => {
    if (!propertyAmenities) return [];
    return propertyAmenities.filter(a => !a.name.startsWith("rentio.default"));
  }, [propertyAmenities]);

  // Helpers
  const getItems = () => {
    if (!property) {
      return [];
    }

    const result: IExtraCardItem[] = [];

    // Everyone who may change the property may change what is on this tab.
    // Bookkeeping is the exception and asks for the two money roles.
    const canEdit = iHaveOneOfRoles(property, [
      EPropertyMemberTypes.Admin,
      EPropertyMemberTypes.FinancialManager,
      EPropertyMemberTypes.TechnicalManager,
      EPropertyMemberTypes.Owner,
    ]);
    const canEditBookkeeping = iHaveOneOfRoles(property, [
      EPropertyMemberTypes.Admin,
      EPropertyMemberTypes.FinancialManager,
    ]);

    const canHaveFloors = propertyTypeCanHaveFloors({ property });
    const canHaveAdjoinedType = propertyTypeCanHaveAdjoinedType({ property });

    if (!isGroup) {
      // General. What a candidate reads first on the Rentmatch page: what it
      // is, what it costs, when it is free and how big it is.
      result.push({
        title: getLocalizedText("property.extra.general.title"),
        extraInfoGroup: EExtraInfoGroup.General,
        items: compact([
          {
            title: getLocalizedText("property.field.type"),
            value: property.typeId
              ? getPropertyTypeString({ typeId: property.typeId })
              : undefined,
          },
          {
            title: getLocalizedText("property_info.form.price.label"),
            value:
              property.price !== undefined && property.price !== null
                ? formatCurrency(property.price)
                : undefined,
          },
          {
            title: getLocalizedText("property_info.form.costs.label"),
            value:
              property.costs !== undefined && property.costs !== null
                ? formatCurrency(property.costs)
                : undefined,
          },
          {
            title: getLocalizedText("property.field.costs_comment"),
            value: amenityText(propertyAmenities, AMENITY_NAMES.costsComment),
          },
          {
            title: getLocalizedText(
              "property_info.form.available_at_date.label",
            ),
            value: property.availableAtDate
              ? formatDate(property.availableAtDate)
              : undefined,
          },
          {
            title: getLocalizedText(
              "property.extra.interior.field.room_count_bedroom",
            ),
            value: amenityNumber(
              propertyAmenities,
              AMENITY_NAMES.roomCountBedroom,
            ),
          },
          {
            title: getLocalizedText(
              "property.extra.interior.field.room_count_bathroom",
            ),
            value: amenityNumber(
              propertyAmenities,
              AMENITY_NAMES.roomCountBathroom,
            ),
          },
          {
            title: getLocalizedText(
              "property.extra.interior.field.living_surface_area",
            ),
            value: amenityMeasure(
              propertyAmenities,
              AMENITY_NAMES.livingSurfaceArea,
              "m²",
            ),
          },
          canHaveFloors && {
            title: getLocalizedText(
              "property.extra.interior.field.building_floor",
            ),
            value: amenityNumber(
              propertyAmenities,
              AMENITY_NAMES.buildingFloor,
            ),
          },
          canHaveFloors && {
            title: getLocalizedText(
              "property.extra.interior.field.building_layers",
            ),
            value: amenityNumber(
              propertyAmenities,
              AMENITY_NAMES.buildingFloorCount,
            ),
          },
        ]),
        editable: canEdit,
        clearable: true,
      });
    }

    if (!isGroup) {
      const hasEpcLabel =
        property.epcLabel &&
        property.epcLabel !== EPropertyEPCLabel.Unknown &&
        property.epcLabel !== EPropertyEPCLabel.NoEPCAvailable;

      result.push({
        title: getLocalizedText("property.extra.epc.title"),
        extraInfoGroup: EExtraInfoGroup.Epc,
        items: compact([
          {
            title: getLocalizedText("editor.variable.property_epc_label"),
            value: property.epcLabel
              ? getLocalizedText(`epc.label.${property.epcLabel}`.toLowerCase())
              : undefined,
          },
          hasEpcLabel && {
            title: getLocalizedText("editor.variable.property_epc_score"),
            value:
              property.epcValue !== null
                ? `${property.epcValue} ${getLocalizedText(
                    "epc.energy_usage.per_year",
                  )}`
                : undefined,
          },
          hasEpcLabel && {
            title: getLocalizedText("editor.variable.property_epc_end_date"),
            value: property.epcExpirationDate
              ? formatDate(property.epcExpirationDate)
              : undefined,
          },
          hasEpcLabel && {
            title: getLocalizedText(
              "editor.variable.property_epc_certificate_id",
            ),
            value: property.epcCertificateId,
          },
          {
            title: getLocalizedText("property.field.e_level"),
            value: amenityNumber(propertyAmenities, AMENITY_NAMES.eLevel),
          },
          {
            title: getLocalizedText("property.field.k_level"),
            value: amenityNumber(propertyAmenities, AMENITY_NAMES.kLevel),
          },
        ]),
        editable: canEdit,
        clearable: true,
      });
    }

    if (!isGroup) {
      // Outside. The garden and the terrace are a yes or no first; their
      // surface and orientation only follow once the answer is yes.
      //
      // Nobody has answered that question yet, because until now the tab only
      // asked for the surface. A property that carries one is therefore read
      // as a yes, so an existing garden keeps its line on this card.
      const hasGarden =
        amenityBoolean(propertyAmenities, AMENITY_NAMES.hasGarden) ??
        (property.buildingGardenSurfaceArea ? true : undefined);
      const hasTerrace =
        amenityBoolean(propertyAmenities, AMENITY_NAMES.hasTerrace) ??
        (property.buildingTerraceSurfaceArea ? true : undefined);

      result.push({
        title: getLocalizedText("property.extra.exterior.title"),
        extraInfoGroup: EExtraInfoGroup.Exterior,
        items: compact([
          {
            title: getLocalizedText("property.field.ground_surface_area"),
            value: amenityMeasure(
              propertyAmenities,
              AMENITY_NAMES.groundSurfaceArea,
              "m²",
            ),
          },
          {
            title: getLocalizedText(
              "property.extra.exterior.field.building_surface_area",
            ),
            value: amenityMeasure(
              propertyAmenities,
              AMENITY_NAMES.buildingSurfaceArea,
              "m²",
            ),
          },
          {
            title: getLocalizedText("property.field.has_garden"),
            value: hasGarden,
          },
          hasGarden && {
            title: getLocalizedText(
              "property.extra.exterior.field.building_garden_surface_area",
            ),
            value:
              property.buildingGardenSurfaceArea !== null &&
              property.buildingGardenSurfaceArea !== undefined
                ? `${property.buildingGardenSurfaceArea} m²`
                : undefined,
          },
          hasGarden && {
            title: getLocalizedText("property.field.garden_orientation"),
            value: amenityOption(
              propertyAmenities,
              AMENITY_NAMES.gardenOrientation,
              "orientation",
            ),
          },
          {
            title: getLocalizedText("property.field.has_terrace"),
            value: hasTerrace,
          },
          hasTerrace && {
            title: getLocalizedText(
              "property.extra.exterior.field.building_terrace_surface_area",
            ),
            value:
              property.buildingTerraceSurfaceArea !== null &&
              property.buildingTerraceSurfaceArea !== undefined
                ? `${property.buildingTerraceSurfaceArea} m²`
                : undefined,
          },
          hasTerrace && {
            title: getLocalizedText("property.field.terrace_orientation"),
            value: amenityOption(
              propertyAmenities,
              AMENITY_NAMES.terraceOrientation,
              "orientation",
            ),
          },
          {
            title: getLocalizedText("property.field.has_garden_shed"),
            value: amenityBoolean(
              propertyAmenities,
              AMENITY_NAMES.hasGardenShed,
            ),
          },
          {
            title: getLocalizedText("property.field.has_pool"),
            value: amenityBoolean(propertyAmenities, AMENITY_NAMES.hasPool),
          },
          {
            title: getLocalizedText(
              "property.field.number_of_parkings_outside",
            ),
            value: amenityNumber(
              propertyAmenities,
              AMENITY_NAMES.numberOfParkingsOutside,
            ),
          },
        ]),
        editable: canEdit,
        clearable: true,
      });
    }

    if (!isGroup) {
      result.push({
        title: getLocalizedText("property.extra.interior.title"),
        extraInfoGroup: EExtraInfoGroup.Interior,
        items: compact([
          {
            title: getLocalizedText("property.field.number_of_toilets"),
            value: amenityNumber(
              propertyAmenities,
              AMENITY_NAMES.numberOfToilets,
            ),
          },
          {
            title: getLocalizedText("property.field.number_of_shower_rooms"),
            value: amenityNumber(
              propertyAmenities,
              AMENITY_NAMES.numberOfShowerRooms,
            ),
          },
          {
            title: getLocalizedText("property.field.has_attic"),
            value: amenityBoolean(propertyAmenities, AMENITY_NAMES.hasAttic),
          },
          {
            title: getLocalizedText(
              "property.extra.interior.field.building_has_storage",
            ),
            value: amenityBoolean(
              propertyAmenities,
              AMENITY_NAMES.buildingHasStorage,
            ),
          },
          {
            title: getLocalizedText(
              "property.extra.interior.field.is_furnished",
            ),
            value: amenityBoolean(propertyAmenities, AMENITY_NAMES.isFurnished),
          },
          {
            title: getLocalizedText("property.field.kitchen_type"),
            value: amenityOption(
              propertyAmenities,
              AMENITY_NAMES.kitchenType,
              "kitchenType",
            ),
          },
          {
            title: getLocalizedText("property.field.floor_type"),
            value: amenityOption(
              propertyAmenities,
              AMENITY_NAMES.floorType,
              "floorType",
            ),
          },
          {
            title: getLocalizedText(
              "property.field.building_has_hard_materials",
            ),
            value: amenityBoolean(
              propertyAmenities,
              AMENITY_NAMES.buildingHasHardMaterials,
            ),
          },
          {
            title: getLocalizedText("property.extra.amenities.item.has_oven"),
            value: amenityBoolean(propertyAmenities, AMENITY_NAMES.hasOven),
          },
          {
            title: getLocalizedText("property.extra.amenities.item.has_fridge"),
            value: amenityBoolean(propertyAmenities, AMENITY_NAMES.hasFridge),
          },
          {
            title: getLocalizedText(
              "property.extra.amenities.item.has_dish_washer",
            ),
            value: amenityBoolean(
              propertyAmenities,
              AMENITY_NAMES.hasDishwasher,
            ),
          },
          {
            title: getLocalizedText(
              "property.extra.amenities.item.has_range_hood",
            ),
            value: amenityBoolean(
              propertyAmenities,
              AMENITY_NAMES.hasRangeHood,
            ),
          },
          {
            title: getLocalizedText(
              "property.extra.interior.field.room_count_garage",
            ),
            value: amenityNumber(
              propertyAmenities,
              AMENITY_NAMES.roomCountGarage,
            ),
          },
          {
            title: getLocalizedText("property.field.number_of_parkings_inside"),
            value: amenityNumber(
              propertyAmenities,
              AMENITY_NAMES.numberOfParkingsInside,
            ),
          },
        ]),
        editable: canEdit,
        clearable: true,
      });
    }

    if (!isGroup) {
      result.push({
        title: getLocalizedText("property.group.comfort"),
        extraInfoGroup: EExtraInfoGroup.Comfort,
        items: compact([
          {
            title: getLocalizedText("property.extra.amenities.item.has_lift"),
            value: amenityBoolean(propertyAmenities, AMENITY_NAMES.hasLift),
          },
          {
            title: getLocalizedText("property.field.has_air_conditioning"),
            value: amenityBoolean(
              propertyAmenities,
              AMENITY_NAMES.hasAirConditioning,
            ),
          },
          {
            title: getLocalizedText("property.field.has_alarm"),
            value: amenityBoolean(propertyAmenities, AMENITY_NAMES.hasAlarm),
          },
          {
            title: getLocalizedText("property.field.has_videophone"),
            value: amenityBoolean(
              propertyAmenities,
              AMENITY_NAMES.hasVideophone,
            ),
          },
          {
            title: getLocalizedText("property.field.has_smoke_detectors"),
            value: amenityBoolean(
              propertyAmenities,
              AMENITY_NAMES.hasSmokeDetectors,
            ),
          },
          {
            title: getLocalizedText("property.field.accessible_for_disabled"),
            value: amenityOption(
              propertyAmenities,
              AMENITY_NAMES.accessibleForDisabled,
              "yesNoUnknown",
            ),
          },
        ]),
        editable: canEdit,
        clearable: true,
      });
    }

    if (!isGroup) {
      result.push({
        title: getLocalizedText("property.extra.energy.title"),
        extraInfoGroup: EExtraInfoGroup.Energy,
        items: compact([
          {
            title: getLocalizedText(
              "property.extra.energy.item.has_central_heating",
            ),
            value: amenityBoolean(
              propertyAmenities,
              AMENITY_NAMES.hasCentralHeating,
            ),
          },
          {
            title: getLocalizedText(
              "property.extra.energy.item.has_heating_type",
            ),
            value: amenityOption(
              propertyAmenities,
              AMENITY_NAMES.heatingType,
              "heatingType",
            ),
          },
          {
            title: getLocalizedText("property.field.heating_distribution"),
            value: amenityOption(
              propertyAmenities,
              AMENITY_NAMES.heatingDistribution,
              "heatingDistribution",
            ),
          },
          {
            title: getLocalizedText(
              "property.extra.energy.item.has_double_glazing",
            ),
            value: amenityBoolean(
              propertyAmenities,
              AMENITY_NAMES.hasDoubleGlazing,
            ),
          },
          {
            title: getLocalizedText("property.field.glazing_type"),
            value: amenityOption(
              propertyAmenities,
              AMENITY_NAMES.glazingType,
              "glazingType",
            ),
          },
          {
            title: getLocalizedText("property.field.window_frame_material"),
            value: amenityOption(
              propertyAmenities,
              AMENITY_NAMES.windowFrameMaterial,
              "windowFrameMaterial",
            ),
          },
          {
            title: getLocalizedText("property.field.has_insulation"),
            value: amenityBoolean(
              propertyAmenities,
              AMENITY_NAMES.hasInsulation,
            ),
          },
          {
            title: getLocalizedText("property.field.has_solar_panels"),
            value: amenityBoolean(
              propertyAmenities,
              AMENITY_NAMES.hasSolarPanels,
            ),
          },
          {
            title: getLocalizedText("property.field.is_low_energy"),
            value: amenityBoolean(propertyAmenities, AMENITY_NAMES.isLowEnergy),
          },
          {
            title: getLocalizedText("property.field.is_passive_house"),
            value: amenityBoolean(
              propertyAmenities,
              AMENITY_NAMES.isPassiveHouse,
            ),
          },
          {
            title: getLocalizedText("property.field.co2_emission"),
            value: amenityMeasure(
              propertyAmenities,
              AMENITY_NAMES.co2Emission,
              "kg CO₂/m²",
            ),
          },
        ]),
        editable: canEdit,
        clearable: true,
      });
    }

    if (!isGroup) {
      result.push({
        title: getLocalizedText("property.group.house_rules"),
        extraInfoGroup: EExtraInfoGroup.HouseRules,
        items: compact([
          {
            title: getLocalizedText("property.field.pets_allowed"),
            value: amenityOption(
              propertyAmenities,
              AMENITY_NAMES.petsAllowed,
              "yesNoUnknown",
            ),
          },
          {
            title: getLocalizedText("property.field.pets_allowed_detail"),
            value: amenityOption(
              propertyAmenities,
              AMENITY_NAMES.petsAllowedDetail,
              "petsAllowedDetail",
            ),
          },
          {
            title: getLocalizedText("property.field.smoking_allowed"),
            value: amenityOption(
              propertyAmenities,
              AMENITY_NAMES.smokingAllowed,
              "yesNoUnknown",
            ),
          },
        ]),
        editable: canEdit,
        clearable: true,
      });
    }

    if (!isGroup) {
      result.push({
        title: getLocalizedText("property.group.condition"),
        extraInfoGroup: EExtraInfoGroup.Condition,
        items: compact([
          {
            title: getLocalizedText("property.field.condition"),
            value: amenityOption(
              propertyAmenities,
              AMENITY_NAMES.condition,
              "condition",
            ),
          },
          {
            title: getLocalizedText(
              "property.extra.general.item.construction_year",
            ),
            value: property.constructionYear,
          },
          {
            title: getLocalizedText("property.field.renovation_year"),
            value: amenityNumber(
              propertyAmenities,
              AMENITY_NAMES.renovationYear,
            ),
          },
          {
            title: getLocalizedText("property.field.environment_type"),
            value: amenityOption(
              propertyAmenities,
              AMENITY_NAMES.environmentType,
              "environmentType",
            ),
          },
        ]),
        editable: canEdit,
        clearable: true,
      });
    }

    if (!isGroup) {
      result.push({
        title: getLocalizedText("property.group.structural"),
        extraInfoGroup: EExtraInfoGroup.Structural,
        items: compact([
          canHaveAdjoinedType && {
            title: getLocalizedText(
              "property.extra.exterior.field.building_adjoined",
            ),
            value: amenityOption(
              propertyAmenities,
              AMENITY_NAMES.buildingAdjoined,
              "adjoined",
            ),
          },
          {
            title: getLocalizedText("property.field.front_width"),
            value: amenityMeasure(
              propertyAmenities,
              AMENITY_NAMES.frontWidth,
              "m",
            ),
          },
          {
            title: getLocalizedText("property.field.volume"),
            value: amenityMeasure(
              propertyAmenities,
              AMENITY_NAMES.volume,
              "m³",
            ),
          },
          {
            title: getLocalizedText("property.field.roof_type"),
            value: amenityOption(
              propertyAmenities,
              AMENITY_NAMES.roofType,
              "roofType",
            ),
          },
          {
            title: getLocalizedText("property.field.roof_material"),
            value: amenityOption(
              propertyAmenities,
              AMENITY_NAMES.roofMaterial,
              "roofMaterial",
            ),
          },
        ]),
        editable: canEdit,
        clearable: true,
      });
    }

    if (!isGroup) {
      result.push({
        title: getLocalizedText("property.group.risk"),
        extraInfoGroup: EExtraInfoGroup.Risk,
        items: compact([
          {
            title: getLocalizedText("property.field.flood_zone_g_score"),
            value: amenityOption(
              propertyAmenities,
              AMENITY_NAMES.floodZoneGScore,
              "floodScore",
            ),
          },
          {
            title: getLocalizedText("property.field.flood_zone_p_score"),
            value: amenityOption(
              propertyAmenities,
              AMENITY_NAMES.floodZonePScore,
              "floodScore",
            ),
          },
          {
            title: getLocalizedText("property.field.conformity_certificate"),
            value: amenityOption(
              propertyAmenities,
              AMENITY_NAMES.conformityCertificate,
              "yesNoUnknown",
            ),
          },
          {
            title: getLocalizedText("property.field.electricity_certificate"),
            value: amenityOption(
              propertyAmenities,
              AMENITY_NAMES.electricityCertificate,
              "electricityCertificate",
            ),
          },
          {
            title: getLocalizedText("property.field.asbestos_certificate"),
            value: amenityText(
              propertyAmenities,
              AMENITY_NAMES.asbestosCertificate,
            ),
          },
          {
            title: getLocalizedText("property.field.asbestos_certificate_date"),
            value: amenityDate(
              propertyAmenities,
              AMENITY_NAMES.asbestosCertificateDate,
            ),
          },
        ]),
        editable: canEdit,
        clearable: true,
      });
    }

    if (!isGroup) {
      // Everything a candidate can look at before standing in the doorway, in
      // the order Rentmatch shows it: the virtual visit leads, then the photos.
      //
      // The four links are stored as amenities and were filled until now only
      // by a partner CRM pushing a property in, so a property entered at Rentio
      // could never have a virtual visit at all.
      result.push({
        title: getLocalizedText("property.extra.media.title"),
        extraInfoGroup: EExtraInfoGroup.Media,
        items: compact([
          {
            title: getLocalizedText("property.extra.media.item.virtual_tour"),
            value: amenityText(propertyAmenities, AMENITY_NAMES.virtualTourUrl),
          },
          // Not a field of this form: the photos have a gallery of their own,
          // with uploading, ordering and deleting. The line is here because
          // this is where somebody comes looking for them.
          {
            title: getLocalizedText("property.extra.media.item.photos"),
            onClick: () => setShowPhotoGallery(true),
          },
          {
            title: getLocalizedText("property.extra.media.item.video"),
            value: amenityText(propertyAmenities, AMENITY_NAMES.videoUrl),
          },
          {
            title: getLocalizedText("property.extra.media.item.model_3d"),
            value: amenityText(propertyAmenities, AMENITY_NAMES.model3dUrl),
          },
          {
            title: getLocalizedText("property.extra.media.item.floor_plan"),
            value: amenityText(propertyAmenities, AMENITY_NAMES.floorPlanUrl),
          },
        ]),
        editable: canEdit,
        clearable: true,
      });
    }

    if (!isGroup) {
      const hasElectricity =
        property.electricityType &&
        property.electricityType !== EElectricityType.Unknown;
      result.push({
        title: getLocalizedText("property.extra.electricity.title"),
        extraInfoGroup: EExtraInfoGroup.Electricity,
        items: compact([
          {
            title: getLocalizedText("move.field.electricity_type.title"),
            value: property.electricityType
              ? getLocalizedText(
                  `move.option.electricity_type.${property.electricityType}`.toLowerCase(),
                )
              : undefined,
          },
          hasElectricity && {
            title: getLocalizedText("move.field.electricity_ean.title"),
            value: property.electricityEan,
          },
          hasElectricity && {
            title: getLocalizedText(
              "move.field.electricity_meter_number_both.title",
            ),
            value: property.electricityMeterNumberBoth,
          },
          hasElectricity && {
            title: getLocalizedText("move.field.electricity_meter_type.title"),
            value: property.electricityMeterType
              ? getLocalizedText(
                  `move.option.electricity_meter_type.${property.electricityMeterType}`.toLowerCase(),
                )
              : undefined,
          },
        ]),
        editable: canEdit,
        clearable: true,
      });

      const hasNaturalGas =
        property.naturalGasAvailable &&
        property.naturalGasAvailable === ENaturalGasAvailable.Available;

      result.push({
        title: getLocalizedText("property.extra.natural_gas.title"),
        extraInfoGroup: EExtraInfoGroup.NaturalGas,
        items: compact([
          {
            title: getLocalizedText("move.field.natural_gas_type.title"),
            value: property.naturalGasAvailable
              ? getLocalizedText(
                  `move.option.natural_gas_type.${property.naturalGasAvailable}`.toLowerCase(),
                )
              : undefined,
          },
          hasNaturalGas && {
            title: getLocalizedText("move.field.natural_gas_ean.title"),
            value: property.naturalGasEan,
          },
          hasNaturalGas && {
            title: getLocalizedText(
              "move.field.natural_gas_meter_number.title",
            ),
            value: property.naturalGasMeterNumber,
          },
          hasNaturalGas && {
            title: getLocalizedText("move.field.natural_gas_meter_type.title"),
            value: property.naturalGasMeterType
              ? getLocalizedText(
                  `move.option.natural_gas_meter_type.${property.naturalGasMeterType}`.toLowerCase(),
                )
              : undefined,
          },
        ]),
        editable: canEdit,
        clearable: true,
      });

      result.push({
        title: getLocalizedText("property.extra.water.title"),
        extraInfoGroup: EExtraInfoGroup.Water,
        items: [
          {
            title: getLocalizedText("move.field.water_meter_number.title"),
            value: property.waterMeterNumber,
          },
        ],
        editable: canEdit,
        clearable: true,
      });
    }

    if (hasBookkeepingFeature) {
      if (isGroup) {
        result.push({
          title: getLocalizedText("property.extra.bookkeeping.title"),
          extraInfoGroup: EExtraInfoGroup.BookkeepingGroup,
          items: [
            {
              title: getLocalizedText(
                "property.extra.bookkeeping.item.cost_center.title",
              ),
              value: property.costCenter,
            },
          ],
          editable: canEditBookkeeping,
          clearable: true,
        });
      } else {
        result.push({
          title: getLocalizedText("property.extra.bookkeeping.title"),
          extraInfoGroup: EExtraInfoGroup.Bookkeeping,
          items: [
            {
              title: getLocalizedText(
                "property.extra.bookkeeping.item.cost_unit.title",
              ),
              value: property.costUnit,
            },
            {
              title: getLocalizedText(
                "property.extra.bookkeeping.item.bookkeeping_paid_out_start_date.title",
              ),
              value: property.bookkeepingPaidOutStartDate
                ? formatDate(property.bookkeepingPaidOutStartDate)
                : undefined,
            },
          ],
          editable: canEditBookkeeping,
          clearable: true,
        });
      }
    }

    return result;
  };

  // Event handlers
  const handleEditSuccess = () => {
    setExtraInfoGroup(undefined);
    refetchPropertyAmenities();
  };

  const handleOpenBulkAmenityModal = () => {
    setIsBulkAmenityModalOpen(true);
  };

  const handleCloseBulkAmenityModal = () => {
    setIsBulkAmenityModalOpen(false);
  };

  const handleSaveBulkAmenities = async (amenities: IEditableAmenity[]) => {
    if (!propertyId) return;

    try {
      // Process custom amenities: filter out empty names, keep valid ones
      const validCustomAmenities = amenities
        .filter(a => a.name.trim())
        .map(a => ({
          id: a.id,
          name: a.name,
          dataType: a.dataType,
          value: a.value,
        }));

      // Find deleted amenities: those that existed before but are no longer in the list
      const remainingAmenityIds = new Set(
        validCustomAmenities.filter(a => a.id).map(a => a.id),
      );

      // Amenities that were deleted (existed before but not in remaining)
      const deletedAmenities = customAmenities
        .filter(a => !remainingAmenityIds.has(a.id))
        .map(a => ({
          id: a.id,
          name: a.name,
          dataType: getDataTypeFromValue(a.value),
          value: null as null, // Send null to trigger deletion
        }));

      // Combine valid custom amenities with deleted amenities (for deletion)
      const amenitiesToUpsert = [...validCustomAmenities, ...deletedAmenities];

      await propertyAmenityApi.upsertForProperty(propertyId, {
        amenities: amenitiesToUpsert,
      });
      refetchPropertyAmenities();
      handleCloseBulkAmenityModal();

      // Show success toast
      showAlert({
        type: "success",
        message: propertyExtraOtherAmenitiesToastSuccess(),
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to save amenities:", error);
    }
  };

  // Render
  if (!property) {
    return null;
  }

  const renderCard = (item: IExtraCardItem) => {
    return (
      <Card
        heading={item.title}
        actions={
          item.editable
            ? [
                {
                  content: getLocalizedText("system.edit"),
                  onClick: () => {
                    setExtraInfoGroup(item.extraInfoGroup);
                  },
                },
              ]
            : undefined
        }
      >
        <ResourceList
          items={item.items}
          renderItem={item => {
            let mappedItemValue = item.value;
            if (typeof item.value === "number") {
              mappedItemValue = `${item.value}`;
            } else if (typeof item.value === "boolean") {
              if (item.value) {
                mappedItemValue = getLocalizedText("system.yes");
              } else {
                mappedItemValue = getLocalizedText("system.no");
              }
            }

            return (
              <ResourceListItem item={item}>
                <TextStyle variation="subdued" element="div">
                  {item.title}
                </TextStyle>
                <div style={{ whiteSpace: "pre-line" }}>
                  {item.onClick ? (
                    <Button appearance="link" onClick={item.onClick}>
                      {getLocalizedText("system.edit")}
                    </Button>
                  ) : (
                    mappedItemValue || "-"
                  )}
                </div>
              </ResourceListItem>
            );
          }}
        />
      </Card>
    );
  };

  const renderOtherAmenitiesCard = () => {
    const canEdit = iHaveOneOfRoles(property, [
      EPropertyMemberTypes.Admin,
      EPropertyMemberTypes.FinancialManager,
      EPropertyMemberTypes.TechnicalManager,
      EPropertyMemberTypes.Owner,
    ]);

    return (
      <Card
        heading={getLocalizedText("property.extra.other_amenities.title")}
        actions={
          canEdit
            ? [
                {
                  content: getLocalizedText("system.edit"),
                  onClick: handleOpenBulkAmenityModal,
                },
              ]
            : undefined
        }
      >
        {customAmenities.length === 0 ? (
          <div style={{ padding: "16px", color: "#637381" }}>
            {getLocalizedText("system.empty_value")}
          </div>
        ) : (
          <ResourceList
            items={customAmenities.map(a => ({
              id: a.id,
              title: a.name,
              value: a.value,
            }))}
            renderItem={(item: {
              id: string;
              title: string;
              value: string | number | boolean;
            }) => {
              let mappedItemValue: string | undefined = undefined;
              if (typeof item.value === "number") {
                mappedItemValue = `${item.value}`;
              } else if (typeof item.value === "boolean") {
                mappedItemValue = item.value
                  ? getLocalizedText("system.yes")
                  : getLocalizedText("system.no");
              } else {
                mappedItemValue = item.value;
              }

              return (
                <ResourceListItem item={item}>
                  <TextStyle variation="subdued" element="div">
                    {item.title}
                  </TextStyle>
                  <div style={{ whiteSpace: "pre-line" }}>
                    {mappedItemValue || "-"}
                  </div>
                </ResourceListItem>
              );
            }}
          />
        )}
      </Card>
    );
  };

  return (
    <>
      <Grid spacing="loose">
        {getItems().map(item => (
          <Grid.Item key={item.title} width={[1, 1, 1 / 3]}>
            {renderCard(item)}
          </Grid.Item>
        ))}
        <Grid.Item key="other-amenities" width={[1, 1, 1 / 3]}>
          {renderOtherAmenitiesCard()}
        </Grid.Item>
      </Grid>

      {property && extraInfoGroup ? (
        <MultiStepForm
          formId={`property-info-${formId}`}
          schemas={createEditPropertyExtraInfoForm({
            property,
            propertyAmenities,
            extraInfoGroup,
          })}
          isPerformingSubmitActionSelector={isPerformingSubmitActionSelector(
            property.id,
          )}
          performingSubmitActionErrorSelector={performingSubmitActionErrorSelector(
            property.id,
          )}
          submitActionCreator={submitActionCreator({
            property,
            propertyAmenities,
            extraInfoGroup,
          })}
          withAside={false}
          asModal={true}
          onSuccess={handleEditSuccess}
          modalProps={{
            shouldCloseOnOverlayClick: true,
            onClose: () => {
              setExtraInfoGroup(undefined);
            },
          }}
          submitLabel={getLocalizedText("system.update")}
          isLoading={false}
        />
      ) : null}

      {showPhotoGallery && propertyId ? (
        <PropertyPhotoGalleryModal
          propertyId={propertyId}
          onClose={() => setShowPhotoGallery(false)}
          onSaved={() => refetchPropertyAmenities()}
        />
      ) : null}

      <BulkAmenityEditorModal
        isOpen={isBulkAmenityModalOpen}
        amenities={customAmenities.map(a => ({
          id: a.id,
          name: a.name,
          dataType: getDataTypeFromValue(a.value),
          value: a.value,
        }))}
        onClose={handleCloseBulkAmenityModal}
        onSave={handleSaveBulkAmenities}
      />
    </>
  );
};
